import { useState } from 'react';

import AddCircleIcon from '@mui/icons-material/AddCircle';
import {
	Box,
	Button,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormControlLabel,
	InputLabel,
	MenuItem,
	Select,
	Stack,
	TextField,
	Typography,
} from '@mui/material';

import { IconSelectorInput } from '@@common';

import { DialogActionModel } from '../../Models/DialogActionModel';
import { DialogOptionModel } from '../../Models/DialogOptionModel';
import ActionsInput from './ActionsInput';

const MAX_WIDTH_OPTIONS: Array<{ label: string; value: DialogOptionModel['MaxWidth'] }> = [
	{ label: 'xs', value: 'Xs' },
	{ label: 'sm', value: 'Sm' },
	{ label: 'md', value: 'Md' },
	{ label: 'lg', value: 'Lg' },
	{ label: 'xl', value: 'Xl' },
];

type DialogInputDialogProps = {
	open: boolean;
	setOpen: (open: boolean) => void;
	dialog: DialogOptionModel | undefined;
	onDialogChange: (dialog: DialogOptionModel | undefined) => void;
};

export function DialogInputDialog({ open, setOpen, dialog, onDialogChange }: DialogInputDialogProps) {
	const [localDialog, setLocalDialog] = useState<DialogOptionModel>(
		dialog || {
			Description: '',
		},
	);

	const handleSave = () => {
		onDialogChange(localDialog);
		setOpen(false);
	};

	const handleCancel = () => {
		setOpen(false);
	};

	const handleAddAction = () => {
		const newAction: DialogActionModel = {
			Type: 'Link',
			Label: 'New Action',
		};
		setLocalDialog({
			...localDialog,
			Actions: [...(localDialog.Actions || []), newAction],
		});
	};

	return (
		<Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
			<DialogTitle>Configure Dialog</DialogTitle>
			<DialogContent>
				<Stack spacing={2} sx={{ mt: 1 }}>
					<TextField
						label="Description"
						value={localDialog.Description || ''}
						onChange={(e) => setLocalDialog({ ...localDialog, Description: e.target.value })}
						fullWidth
						multiline
						rows={2}
						autoFocus
					/>
					<IconSelectorInput
						label="Icon"
						value={localDialog.IconName}
						onValueChange={(iconName) => setLocalDialog({ ...localDialog, IconName: iconName })}
						placeholder="Select an icon"
					/>
					<Box>
						<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
							<Typography variant="subtitle2">Actions</Typography>
							<Button size="small" startIcon={<AddCircleIcon />} onClick={handleAddAction}>
								Add Action
							</Button>
						</Box>
						<ActionsInput actions={localDialog.Actions} onActionsChange={(actions) => setLocalDialog({ ...localDialog, Actions: actions })} />
					</Box>
					<Typography variant="subtitle2" color="text.secondary">
						Display Options
					</Typography>
					<FormControlLabel
						control={
							<Checkbox
								checked={localDialog.FullScreen ?? false}
								onChange={(e) => setLocalDialog({ ...localDialog, FullScreen: e.target.checked || undefined })}
							/>
						}
						label="Full Screen"
					/>
					<FormControlLabel
						control={
							<Checkbox
								checked={localDialog.FullWidth ?? false}
								onChange={(e) => setLocalDialog({ ...localDialog, FullWidth: e.target.checked || undefined })}
							/>
						}
						label="Full Width"
					/>
					<FormControl size="small" fullWidth>
						<InputLabel>Max Width</InputLabel>
						<Select
							label="Max Width"
							value={localDialog.MaxWidth ?? ''}
							onChange={(e) => setLocalDialog({ ...localDialog, MaxWidth: (e.target.value as DialogOptionModel['MaxWidth']) || undefined })}
						>
							<MenuItem value="">
								<em>None (xl)</em>
							</MenuItem>
							{MAX_WIDTH_OPTIONS.map((opt) => (
								<MenuItem key={opt.value} value={opt.value}>
									{opt.label}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleCancel}>Cancel</Button>
				<Button onClick={handleSave} variant="contained">
					Save
				</Button>
			</DialogActions>
		</Dialog>
	);
}
